'use client';

import { QueryKeys } from '@/constants/query-keys';
import { revalidateTimes } from '@/constants/revalidate-times';
import { policyPagesApiService } from '@/services/(frontend)/content/legal-policies-api.service';
import { FooterLegalPolicies } from '@/types/legal-policies';
import { parseCacheControlMaxAge } from '@/utils/cache-control';
import { useQuery } from '@tanstack/react-query';
import { useDynamicStaleTime } from '../shared/dynamic-stale-time';

const emptyFooterPolicyPages: FooterLegalPolicies = {
  privacyPolicy: null,
  termsAndConditions: null,
};

export const useFooterLegalPolicies = (enabled: boolean): ReturnType<typeof useQuery<FooterLegalPolicies>> => {
  const updateQueryStaleTime = useDynamicStaleTime();

  return useQuery<FooterLegalPolicies>({
    enabled,
    gcTime: revalidateTimes.fiveMinInMs,
    queryFn: async ({ signal }) => {
      try {
        const response = await policyPagesApiService.getFooterPolicyPages(signal);
        const maxAge = parseCacheControlMaxAge(response.headers?.get('cache-control'));

        if (response.headers && maxAge > revalidateTimes.fiveMinInMs) {
          updateQueryStaleTime(QueryKeys.global.policyPages, response.headers);
        }

        return response.data ?? emptyFooterPolicyPages;
      } catch {
        return emptyFooterPolicyPages;
      }
    },
    queryKey: QueryKeys.global.policyPages,
    staleTime: revalidateTimes.fiveMinInMs,
  });
};
